import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useMatch, useNavigate } from 'react-router'
import { Avatar } from '../../components/Avatar'
import { ConnectionStatus } from '../../components/ConnectionStatus'
import { Icon } from '../../components/Icon'
import { db, type LocalChannel } from '../../db/db'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { useUserMap } from '../../hooks/useUsers'
import { pluralize } from '../../lib/format'
import { MOBILE_QUERY } from '../../lib/platform'
import { isAdminRole, useSession } from '../../state/session'
import { useUi } from '../../state/ui'
import { leaveConversation, LeaveConfirmDialog } from '../channels/leave'
import { IosInstallHint } from '../shell/IosInstallHint'
import { AdminChannelList } from './AdminChannelList'
import { ConversationRow } from './ConversationRow'
import { filterChannels, quietCount, totalUnread, type ConversationFilter } from './filterChannels'
import { NewConversationMenu } from './NewConversationMenu'

const EMPTY: LocalChannel[] = []
const tabs: { id: ConversationFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'channels', label: 'Channels' },
  { id: 'groups', label: 'Groups' },
]

export function ConversationList() {
  const me = useSession((s) => s.me)
  const channels = useLiveQuery(() => db.channels.toArray(), [], EMPTY)
  const users = useUserMap()
  const [filter, setFilter] = useState<ConversationFilter>('all')
  const [query, setQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [onlyQuiet, setOnlyQuiet] = useState(false)
  const [adminView, setAdminView] = useState(false)
  const active = useMatch('/c/:channelId')?.params.channelId
  const isAdmin = isAdminRole(me)
  const phone = useMediaQuery(MOBILE_QUERY)
  const pendingDevices = me?.pendingDeviceRequests ?? 0
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const leaving = useUi((s) => s.leaving)
  const closeLeave = useUi((s) => s.closeLeave)
  const itemsRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  /** After leaving from the list: the row to focus once the dialog has closed ('' for the search field). */
  const focusAfterLeave = useRef<string | null>(null)
  /** The conversation whose row has focus, so focus can move on when that row goes. */
  const focusedRow = useRef<string | null>(null)

  const rows = useMemo(
    () => filterChannels(channels, { filter, query, showArchived, onlyQuiet, users, meId: me?.id ?? null }),
    [channels, filter, query, showArchived, onlyQuiet, users, me?.id],
  )
  const unread = totalUnread(channels)
  const archivedCount = channels.filter((c) => c.isMember && c.archivedAt).length
  const quiet = quietCount(channels)
  const shownRows = useRef(rows)

  const rowLink = (channelId: string) =>
    [...(itemsRef.current?.querySelectorAll<HTMLElement>('.conversation-item') ?? [])].find((item) => item.dataset.channelId === channelId)?.querySelector<HTMLElement>('a')

  const leave = async () => {
    if (!leaving || !me) return
    const index = rows.findIndex((row) => row.channel.id === leaving.channelId)
    const next = rows[index + 1] ?? rows[index - 1]
    if (!(await leaveConversation(leaving.channelId, me.id, leaving.name))) return
    if (leaving.channelId === active) navigate('/')
    focusAfterLeave.current = next && index >= 0 ? next.channel.id : ''
  }

  // The left conversation's row goes: focus moves to the next one (the dialog has put it back on the row by now).
  useEffect(() => {
    const id = focusAfterLeave.current
    if (leaving || id === null) return
    focusAfterLeave.current = null
    ;((id && rowLink(id)) || searchRef.current)?.focus()
  }, [leaving])

  // Which row has focus. Native listeners: React ignores focus events while it commits, and a sheet gives focus back
  // to its row as it closes, during a commit.
  useEffect(() => {
    const items = itemsRef.current
    if (!items) return
    const onFocusIn = (event: FocusEvent) => {
      focusedRow.current = (event.target as Element).closest<HTMLElement>('.conversation-item')?.dataset.channelId ?? null
    }
    const onFocusOut = (event: FocusEvent) => {
      // Focus going elsewhere forgets the row; a row removed with focus in it keeps it, to move focus on. Chromium fires
      // focusout while it removes the focused element, still connected: whether it went is known once the removal is done.
      const target = event.target as Node
      if (items.contains(event.relatedTarget as Node | null)) return
      queueMicrotask(() => {
        if (target.isConnected) focusedRow.current = null
      })
    }
    items.addEventListener('focusin', onFocusIn)
    items.addEventListener('focusout', onFocusOut)
    return () => {
      items.removeEventListener('focusin', onFocusIn)
      items.removeEventListener('focusout', onFocusOut)
    }
  }, [adminView])

  // A row that goes while it has focus (read under Unread, archived, removed) hands focus to the row taking its place.
  useLayoutEffect(() => {
    const before = shownRows.current
    shownRows.current = rows
    const id = focusedRow.current
    if (!id || rows.some((row) => row.channel.id === id)) return
    focusedRow.current = null
    if (document.activeElement && document.activeElement !== document.body) return
    const next = rows[Math.min(Math.max(0, before.findIndex((row) => row.channel.id === id)), rows.length - 1)]
    ;((next && rowLink(next.channel.id)) || searchRef.current)?.focus()
  }, [rows])

  // A confirmation still open as the app goes elsewhere would stay modal out of sight (the list is hidden on phones).
  useEffect(() => {
    closeLeave()
  }, [pathname, closeLeave])

  return (
    <section className="conversations" aria-label="Conversations">
      <header className="chat-brand">
        <Link to="/" className="chat-wordmark" aria-label="BOSS Chat home">
          BOSS <span>Chat</span>
        </Link>
        {phone ? null : <NewConversationMenu />}
      </header>

      <div className="list-controls">
        <div className="list-title-row">
          <div className="messages-title">
            <h1>{adminView ? 'All channels' : 'Messages'}</h1>
            {!adminView && unread > 0 ? (
              <span className="badge" aria-label={`${unread} unread messages`}>
                {unread}
              </span>
            ) : null}
            <ConnectionStatus />
          </div>
          <nav className="list-nav" aria-label="App">
            {isAdmin ? (
              <button
                type="button"
                className="icon-button"
                aria-pressed={adminView}
                aria-label={adminView ? 'Back to my conversations' : 'All channels (admin)'}
                title={adminView ? 'Back to my conversations' : 'All channels (admin)'}
                onClick={() => setAdminView((v) => !v)}
              >
                <Icon name="eye" />
              </button>
            ) : null}
            {phone ? <NewConversationMenu compact /> : null}
            <NavLink to="/search" className="icon-button" aria-label="Search messages" title="Search messages">
              <Icon name="search" />
            </NavLink>
            {isAdmin ? (
              <NavLink
                to={pendingDevices > 0 ? '/admin/devices' : '/admin'}
                className="icon-button"
                aria-label={pendingDevices > 0 ? `Administration, ${pendingDevices} ${pendingDevices === 1 ? 'device' : 'devices'} waiting for approval` : 'Administration'}
                title="Administration"
              >
                <Icon name="shield" />
                {pendingDevices > 0 ? (
                  <span className="badge nav-badge" aria-hidden="true">
                    {pendingDevices > 99 ? '99+' : pendingDevices}
                  </span>
                ) : null}
              </NavLink>
            ) : null}
            <NavLink to="/settings" className="icon-button" aria-label="Settings" title="Settings">
              <Icon name="settings" />
            </NavLink>
          </nav>
        </div>

        <div className="search-field">
          <Icon name="search" />
          <input
            ref={searchRef}
            type="search"
            placeholder={adminView ? 'Filter all channels' : 'Search conversations'}
            aria-label={adminView ? 'Filter all channels' : 'Search conversations'}
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query ? (
            <button type="button" className="icon-button clear-search" aria-label="Clear conversation search" onClick={() => setQuery('')}>
              <Icon name="x" />
            </button>
          ) : null}
        </div>

        {adminView ? null : (
          <>
            <div className="filter-tabs" role="tablist" aria-label="Conversation type">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`filter-${tab.id}`}
                  className="filter-tab"
                  aria-selected={filter === tab.id}
                  tabIndex={filter === tab.id ? 0 : -1}
                  onClick={() => setFilter(tab.id)}
                  onKeyDown={(e) => {
                    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
                    const index = tabs.findIndex((t) => t.id === filter)
                    const next = tabs[(index + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]
                    setFilter(next.id)
                    document.getElementById(`filter-${next.id}`)?.focus()
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <IosInstallHint compact />
            <div className="list-section-label">
              <span>{pluralize(rows.length, 'conversation')}</span>
              {quiet > 0 ? (
                <label className="archived-toggle">
                  <input type="checkbox" checked={onlyQuiet} onChange={(e) => setOnlyQuiet(e.target.checked)} />
                  Show muted ({quiet})
                </label>
              ) : null}
              {archivedCount > 0 ? (
                <label className="archived-toggle">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                  Show archived ({archivedCount})
                </label>
              ) : null}
            </div>
          </>
        )}
      </div>

      {adminView ? (
        <AdminChannelList query={query} activeId={active} />
      ) : (
        <div className="conversation-items" role="tabpanel" aria-labelledby={`filter-${filter}`} ref={itemsRef}>
          {rows.length ? (
            rows.map((row) => (
              <ConversationRow key={row.channel.id} channel={row.channel} name={row.name} users={users} active={row.channel.id === active} />
            ))
          ) : (
            <div className="empty-state">
              <Icon name="search" />
              <strong>{channels.length ? 'No conversations found' : 'No conversations yet'}</strong>
              <p>{channels.length ? 'Try another name or a different filter.' : 'Start a direct message or create a channel.'}</p>
              {query || filter !== 'all' ? (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => {
                    setQuery('')
                    setFilter('all')
                  }}
                >
                  Clear search and filters
                </button>
              ) : null}
            </div>
          )}
        </div>
      )}

      <LeaveConfirmDialog open={leaving !== null} name={leaving?.name ?? ''} onClose={closeLeave} onConfirm={leave} />

      {me ? (
        <footer className="chat-account-footer">
          <Link to="/settings" className="chat-account" aria-label={`Your profile and settings: ${me.displayName}`}>
            <Avatar user={me} online />
            <span>
              <strong>{me.displayName}</strong>
              <small>@{me.username}</small>
            </span>
            <span className="account-label">You</span>
          </Link>
        </footer>
      ) : null}
    </section>
  )
}
